import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
} from "@mui/material";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ArticleIcon from "@mui/icons-material/Article";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PeopleIcon from "@mui/icons-material/People";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PaymentIcon from "@mui/icons-material/Payment";
import CampaignIcon from "@mui/icons-material/Campaign";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
// import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

const drawerWidth = 260;

// Trading platform menu items (trading tools)
const tradingMenuItems = [
  {
    text: "Risk Management",
    icon: <AccountBalanceWalletIcon />,
    path: "/user/risk",
  },
  { text: "Trade History", icon: <HistoryIcon />, path: "/user/history" },
  { text: "Execution", icon: <PlayArrowIcon />, path: "/user/execution" },
  {
    text: "Strategy Dashboard",
    icon: <DashboardIcon />,
    path: "/user/strategy-dashboard",
  },
  {
    text: "Strategy Builder",
    icon: <AccountTreeIcon />,
    path: "/user/strategy-builder",
  },
  {
    text: "Analytics Dashboard",
    icon: <DashboardIcon />,
    path: "/user/analytics",
  },
  {
    text: "Documentation",
    icon: <MenuBookIcon />,
    path: "/user/documentation",
  },
  // Monitor — dev tool (behind feature flag)
  ...(process.env.REACT_APP_ENABLE_MONITOR_PAGE !== 'false' ? [{
    text: "Monitor",
    icon: <SettingsIcon />,
    path: "/user/monitor",
  }] : []),
];

// User features menu items
const userMenuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/user/dashboard" },
  {
    text: "Subscription Plans",
    icon: <SubscriptionsIcon />,
    path: "/user/plans",
  },
  {
    text: "Support Tickets",
    icon: <ConfirmationNumberIcon />,
    path: "/user/tickets",
  },
  { text: "Account Settings", icon: <SettingsIcon />, path: "/user/settings" },
];

// Admin features menu items
const adminMenuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/admin/dashboard" },
  { text: "User Management", icon: <PeopleIcon />, path: "/admin/users" },
  {
    text: "Subscription Plans",
    icon: <SubscriptionsIcon />,
    path: "/admin/plans",
  },
  {
    text: "Support Tickets",
    icon: <ConfirmationNumberIcon />,
    path: "/admin/tickets",
  },
  {
    text: "Payment Integration",
    icon: <PaymentIcon />,
    path: "/admin/payments",
  },
  { text: "Marketing", icon: <CampaignIcon />, path: "/admin/marketing" },
  { text: "Blog Management", icon: <ArticleIcon />, path: "/admin/blog" },
  { text: "Newsletter", icon: <RssFeedIcon />, path: "/admin/newsletter" },
  { text: "System Settings", icon: <SettingsIcon />, path: "/admin/settings" },
];

function Layout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [tradingExpanded, setTradingExpanded] = useState(true);
  const [userExpanded, setUserExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Real auth from JWT context
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const userRole = isAuthenticated ? (isAdmin ? 'admin' : 'user') : null;

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/');
  };

  // Auto-close drawer on mobile when navigating
  useEffect(() => {
    if (isMobile) setOpen(false);
    else setOpen(true);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationsMenu = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  // Function to toggle section expansion
  const toggleSection = (section) => {
    if (section === "trading") {
      setTradingExpanded(!tradingExpanded);
    } else if (section === "user") {
      setUserExpanded(!userExpanded);
    } else if (section === "admin") {
      setAdminExpanded(!adminExpanded);
    }
  };

  // Use effect to auto-expand the relevant section based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/user")) {
      setUserExpanded(true);
      // Close other sections for cleaner navigation when in user area
      setTradingExpanded(false);
      setAdminExpanded(false);
    } else if (path.startsWith("/admin")) {
      setAdminExpanded(true);
      // Close other sections for cleaner navigation when in admin area
      setTradingExpanded(false);
      setUserExpanded(false);
    } else {
      setTradingExpanded(true);
      // Show user section by default as it's more commonly needed
      setUserExpanded(true);
      // Only show admin if user is an admin
      setAdminExpanded(userRole === "admin");
    }
  }, [location.pathname, userRole]);

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: open ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { xs: 0, md: open ? `${drawerWidth}px` : 0 },
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          background: 'rgba(18,23,30,0.8)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.4)',
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            {!open && (
              <Box
                component="img"
                src="/assets/logo-animated.svg"
                alt="AITrader.gold"
                sx={{ height: 28, mr: 1 }}
              />
            )}
            {location.pathname.startsWith("/admin") && (
              <Chip
                label="ADMIN AREA"
                size="small"
                sx={{ ml: 2, fontWeight: 700, fontSize: '0.65rem', bgcolor: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }}
              />
            )}
            {location.pathname.startsWith("/user") && (
              <Chip
                label="USER AREA"
                size="small"
                sx={{ ml: 2, fontWeight: 700, fontSize: '0.65rem', bgcolor: 'rgba(16,185,129,0.12)', color: '#00FF94', border: '1px solid rgba(16,185,129,0.25)' }}
              />
            )}
          </Box>

          {/* User options based on authentication status */}
          {userRole ? (
            <Box sx={{ display: "flex" }}>
              {/* Notifications */}
              <Tooltip title="Notifications">
                <IconButton
                  size="large"
                  color="inherit"
                  onClick={handleNotificationsMenu}
                >
                  <Badge badgeContent={4} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Menu
                id="notifications-menu"
                anchorEl={notificationsAnchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(notificationsAnchorEl)}
                onClose={handleNotificationsClose}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    width: 320,
                    overflow: "visible",
                    mt: 1.5,
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ p: 2, pb: 1 }}
                  fontWeight="bold"
                >
                  Notifications
                </Typography>
                <Divider />
                <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                  <MenuItem sx={{ backgroundColor: 'rgba(255, 243, 205, 0.3)', '&:hover': { backgroundColor: 'rgba(255, 243, 205, 0.5)' } }}>
                    <Box sx={{ width: "100%", display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <WarningAmberIcon sx={{ color: "#FBBF24", fontSize: 18, mt: 0.3 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: '#FBBF24' }}>
                          Exchange Status
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Live trading: Binance Spot & KuCoin. Other exchanges in development.
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleNotificationsClose}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body2" fontWeight="bold">
                        New Feature Available
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Try our new AI strategy builder
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={handleNotificationsClose}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body2" fontWeight="bold">
                        Subscription Update
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your subscription will renew in 3 days
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={handleNotificationsClose}>
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body2" fontWeight="bold">
                        Support Ticket Response
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Your ticket #1234 has been updated
                      </Typography>
                    </Box>
                  </MenuItem>
                </Box>
                <Divider />
                <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
                  <Button size="small" onClick={handleNotificationsClose}>
                    View All
                  </Button>
                </Box>
              </Menu>

              {/* User Menu */}
              <Tooltip title="Account settings">
                <IconButton onClick={handleMenu} color="inherit" sx={{ ml: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor:
                        userRole === "admin"
                          ? "primary.main"
                          : "secondary.main",
                    }}
                  >
                    {userRole === "admin" ? (
                      <AdminPanelSettingsIcon />
                    ) : (
                      <PersonIcon />
                    )}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    minWidth: 180,
                    overflow: "visible",
                    mt: 1.5,
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body1" fontWeight="bold">
                    {user?.displayName || user?.email || (isAdmin ? "Admin" : "User")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isAdmin ? "Administrator" : (user?.planId || "User")}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate(
                      userRole === "admin"
                        ? "/admin/dashboard"
                        : "/user/dashboard",
                    );
                  }}
                >
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  Dashboard
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate(
                      userRole === "admin"
                        ? "/admin/settings"
                        : "/user/settings",
                    );
                  }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box>
              <Button
                color="inherit"
                onClick={() => navigate("/login")}
                startIcon={<LoginIcon />}
                sx={{ mr: 1 }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/register")}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : undefined}
        onClose={isMobile ? handleDrawerToggle : undefined}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: 'rgba(13,17,23,0.85)',
            borderRight: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '2px 0 30px rgba(0,0,0,0.4)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            ...(!isMobile && open
              ? {
                  transition: (theme) =>
                    theme.transitions.create("width", {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.enteringScreen,
                    }),
                }
              : !isMobile ? {
                  overflowX: "hidden",
                  transition: (theme) =>
                    theme.transitions.create("width", {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                    }),
                  width: "64px",
                } : {}),
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "center", height: 64, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {open ? (
            <Box
              component="img"
              src="/assets/logo-animated.svg"
              alt="AITrader.gold"
              sx={{ height: 32, maxWidth: '90%' }}
            />
          ) : (
            <Box
              component="img"
              src="/assets/logo-animated.svg"
              alt="AITrader.gold"
              sx={{ height: 24, width: 24, objectFit: 'contain', objectPosition: 'left' }}
            />
          )}
        </Toolbar>
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.05)' }} />

        {/* Display Trading Platform section only if not in Admin area */}
        {!location.pathname.startsWith("/admin") && (
          <List sx={{ py: 0 }}>
            <ListItem
              disablePadding
              sx={{ display: "block" }}
              onClick={() => toggleSection("trading")}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor: tradingExpanded
                    ? 'rgba(0,255,148,0.06)'
                    : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    color: '#00FF94',
                  }}
                >
                  <ShowChartIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Trading Platform"
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem', color: 'white' }}
                  sx={{ opacity: open ? 1 : 0 }}
                  // Removed aria-hidden to avoid accessibility violations
                />
                {open &&
                  (tradingExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </ListItem>

            <Collapse in={tradingExpanded && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {tradingMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={
                        location.pathname === item.path ||
                        (item.path === "/" && location.pathname === "")
                      }
                      sx={{
                        minHeight: 40,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        pl: open ? 4 : 2.5,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(0,255,148,0.08)',
                          borderRight: '2px solid #00FF94',
                          '&:hover': { bgcolor: 'rgba(0,255,148,0.12)' },
                        },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                          color: (location.pathname === item.path) ? '#00FF94' : '#9CA3AF',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ fontSize: '0.85rem', color: (location.pathname === item.path) ? 'white' : '#D1D5DB' }}
                        sx={{ opacity: open ? 1 : 0 }}
                        // Removed aria-hidden to avoid accessibility violations
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        )}

        <Box sx={{ my: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }} />

        {/* User Section - show for all logged-in users */}
        {userRole && (
          <>
            <List sx={{ py: 0 }}>
              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => toggleSection("user")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    background: userExpanded
                      ? 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.08) 100%)'
                      : 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.06) 100%)',
                    border: '1px solid rgba(16,185,129,0.2)',
                    borderRadius: 1,
                    mx: 0.5,
                    "&:hover": {
                      background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(16,185,129,0.12) 100%)',
                    },
                    color: "white",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: '#00FF94',
                    }}
                  >
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="USER AREA"
                    primaryTypographyProps={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em' }}
                    sx={{ opacity: open ? 1 : 0, color: '#00FF94' }}
                    aria-hidden={!open}
                  />
                  {open &&
                    (userExpanded ? (
                      <ExpandLessIcon sx={{ color: '#00FF94' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#00FF94' }} />
                    ))}
                </ListItemButton>
              </ListItem>

              <Collapse in={userExpanded && open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {userMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        selected={location.pathname === item.path}
                        sx={{
                          minHeight: 40,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                          pl: open ? 4 : 2.5,
                          '&.Mui-selected': {
                            bgcolor: 'rgba(0,255,148,0.08)',
                            borderRight: '2px solid #00FF94',
                            '&:hover': { bgcolor: 'rgba(0,255,148,0.12)' },
                          },
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                            color: (location.pathname === item.path) ? '#00FF94' : '#9CA3AF',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontSize: '0.85rem', color: (location.pathname === item.path) ? 'white' : '#D1D5DB' }}
                          sx={{ opacity: open ? 1 : 0 }}
                          // Removed aria-hidden to avoid accessibility violations
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>

            <Box sx={{ my: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }} />
          </>
        )}

        {/* Admin Section - always show for admin users */}
        {userRole === "admin" && (
          <List sx={{ py: 0 }}>
            <ListItem
              disablePadding
              sx={{ display: "block" }}
              onClick={() => toggleSection("admin")}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor: adminExpanded ? "error.dark" : "error.main",
                  borderRadius: 1,
                  mx: 0.5,
                  "&:hover": {
                    backgroundColor: "error.dark",
                  },
                  color: "white",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="ADMIN AREA"
                  primaryTypographyProps={{ fontWeight: "bold" }}
                  sx={{ opacity: open ? 1 : 0, color: "white" }}
                />
                {open &&
                  (adminExpanded ? (
                    <ExpandLessIcon sx={{ color: "white" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: "white" }} />
                  ))}
              </ListItemButton>
            </ListItem>

            <Collapse in={adminExpanded && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {adminMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={location.pathname === item.path}
                      sx={{
                        minHeight: 40,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        pl: open ? 4 : 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{ opacity: open ? 1 : 0 }}
                        // Removed aria-hidden to avoid accessibility violations
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        )}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${open ? drawerWidth : 64}px)` },
          ml: { xs: 0, md: open ? `${drawerWidth}px` : '64px' },
          pt: { xs: 1, sm: 3 },
          transition: (theme) =>
            theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          backgroundColor: "background.default",
          borderRadius: open && !isMobile ? "8px 0 0 0" : 0,
          boxShadow: open && !isMobile ? "inset 5px 5px 10px rgba(0, 0, 0, 0.05)" : "none",
          height: "100%",
          overflow: "auto",
        }}
      >
        <Toolbar sx={{ mb: { xs: 0, sm: 1 } }} />
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
